<template>
  <footer class="app-footer">
    <div class="footer-container">
      <div class="footer-grid">
        <!-- Brand Column -->
        <div class="footer-brand">
          <router-link to="/" class="footer-logo">
            <img :src="'/media/recraft/logo/denvstack-icon-dark.svg'" alt="DenvStack" class="footer-logo-icon" />
            <span>DenvStack</span>
          </router-link>
          <p class="footer-description">{{ $t('footer.tagline') }}</p>
          <div class="footer-social">
            <a href="https://github.com" target="_blank" rel="noopener noreferrer" class="social-link" aria-label="GitHub">
              <i class="fab fa-github"/>
            </a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" class="social-link" aria-label="LinkedIn">
              <i class="fab fa-linkedin-in"/>
            </a>
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" class="social-link" aria-label="Twitter">
              <i class="fab fa-twitter"/>
            </a>
          </div>
        </div>

        <!-- Who it's for — the five persona doors (src/utils/persona.js). -->
        <div class="footer-column">
          <h4 class="footer-heading">{{ $t('footer.whoFor') }}</h4>
          <ul class="footer-links">
            <li v-for="door in doors" :key="door.id">
              <router-link :to="door.slug">{{ $t(door.labelKey) }}</router-link>
            </li>
          </ul>
        </div>

        <!-- Product Column -->
        <div class="footer-column">
          <h4 class="footer-heading">{{ $t('footer.product') }}</h4>
          <ul class="footer-links">
            <li><router-link to="/pricing">{{ $t('footer.links.pricing') }}</router-link></li>
            <li><router-link to="/marketplace">{{ $t('footer.links.marketplace') }}</router-link></li>
            <li><router-link to="/platform">{{ $t('footer.links.platform') }}</router-link></li>
            <li><router-link to="/security">{{ $t('footer.links.security') }}</router-link></li>
            <!-- /docs is served by platform-dashboard behind the ingress: a full page load, not an SPA route. -->
            <li><a href="/docs">{{ $t('footer.links.docs') }}</a></li>
            <li><a href="/api/swagger/ui" target="_blank" rel="noopener noreferrer">{{ $t('footer.links.api') }}</a></li>
          </ul>
        </div>

        <!--
          Legal column — added for Paddle merchant review. Every page in the
          /terms /privacy /refunds /aup /dpa /subprocessors /security /contact
          set must be reachable from any page of the site, hence its place in
          the global footer. Don't bury individual links — Paddle reviewers
          look for the whole set.
        -->
        <div class="footer-column">
          <h4 class="footer-heading">{{ $t('footer.legal') }}</h4>
          <ul class="footer-links">
            <li><router-link to="/terms">Terms of Service</router-link></li>
            <li><router-link to="/privacy">Privacy Policy</router-link></li>
            <li><router-link to="/refunds">Refund Policy</router-link></li>
            <li><router-link to="/aup">Acceptable Use</router-link></li>
            <li><router-link to="/dpa">Data Processing Addendum</router-link></li>
            <li><router-link to="/subprocessors">Subprocessors</router-link></li>
            <li><router-link to="/security">Security</router-link></li>
            <li><router-link to="/contact">Contact</router-link></li>
          </ul>
        </div>

        <!-- Company — there is no About page; every entry here lands on the
             contact form, pre-set to the right enquiry where one exists. -->
        <div class="footer-column">
          <h4 class="footer-heading">{{ $t('footer.company') }}</h4>
          <ul class="footer-links">
            <li><router-link to="/contact">{{ $t('footer.links.contact') }}</router-link></li>
            <li><router-link to="/contact?topic=walkthrough">{{ $t('footer.links.walkthrough') }}</router-link></li>
            <li><router-link to="/contact?topic=self-managed">{{ $t('footer.links.selfManaged') }}</router-link></li>
          </ul>
        </div>
      </div>

      <!-- Newsletter Section -->
      <div class="footer-newsletter">
        <div class="newsletter-content">
          <h4 class="newsletter-heading">Stay up to date</h4>
          <p class="newsletter-text">Get the latest updates on new features and releases.</p>
        </div>
        <form class="newsletter-form" @submit.prevent>
          <input
            type="email"
            class="newsletter-input"
            placeholder="Enter your email"
            aria-label="Email address"
          />
          <button type="submit" class="newsletter-btn">Subscribe</button>
        </form>
      </div>

      <!-- Tech Stack Icons -->
      <div class="footer-tech-stack">
        <span class="tech-stack-label">Powered by</span>
        <div class="tech-stack-icons">
          <img :src="'/media/recraft/icons/kubernetes.svg'" alt="Kubernetes" title="Kubernetes" />
          <img :src="'/media/recraft/icons/docker.svg'" alt="Docker" title="Docker" />
          <img :src="'/media/recraft/icons/python.svg'" alt="Python" title="Python" />
          <img :src="'/media/recraft/icons/vuejs.svg'" alt="Vue.js" title="Vue.js" />
          <img :src="'/media/recraft/icons/mongodb.svg'" alt="MongoDB" title="MongoDB" />
          <img :src="'/media/recraft/icons/gitlab.svg'" alt="GitLab" title="GitLab" />
        </div>
      </div>

      <!-- Bottom Bar -->
      <div class="footer-bottom">
        <p class="footer-copyright">
          &copy; 2026 <strong>Caucasus Systems LLC</strong> — DenvStack is a product of Caucasus Systems LLC, Georgia.
          <span class="footer-copyright-dot" aria-hidden="true">·</span>
          All rights reserved.
        </p>
        <p class="footer-copyright footer-copyright--legal">
          <router-link to="/terms">Terms</router-link>
          <span aria-hidden="true">·</span>
          <router-link to="/privacy">Privacy</router-link>
          <span aria-hidden="true">·</span>
          <router-link to="/refunds">Refunds</router-link>
          <span aria-hidden="true">·</span>
          <router-link to="/dpa">DPA</router-link>
          <span aria-hidden="true">·</span>
          <router-link to="/security">Security</router-link>
        </p>
      </div>
    </div>
  </footer>
</template>

<script>
import { DOORS } from '@/utils/persona'

export default {
  name: 'AppFooter',
  data() {
    return { doors: DOORS }
  }
}
</script>

<style scoped>
.app-footer {
  background: var(--ds-primary-900);
  color: rgba(255, 255, 255, 0.8);
  padding: 4rem 0 0;
  margin-top: auto;
}

.footer-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.footer-grid {
  /* Brand · Who it's for · Product · Legal (Paddle review) · Company. */
  display: grid;
  grid-template-columns: 1.4fr 1.25fr 1fr 1.15fr 1.1fr;
  gap: 2.5rem;
  padding-bottom: 3rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

/* Brand Column */
.footer-logo {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  font-size: 1.4rem;
  font-weight: 700;
  font-family: var(--ds-font-display);
  color: var(--ds-white);
  margin-bottom: 1rem;
}

.footer-logo-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.footer-description {
  font-size: 0.9rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 1.5rem;
  max-width: 300px;
}

.footer-social {
  display: flex;
  gap: 0.75rem;
}

.social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: var(--ds-radius-md);
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1rem;
}

.social-link:hover {
  background: var(--ds-primary-500);
  color: var(--ds-white);
  transform: translateY(-2px);
}

/* Footer Columns */
.footer-heading {
  font-family: var(--ds-font-display);
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--ds-white);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1.25rem;
}

.footer-links {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-links li {
  margin-bottom: 0.75rem;
}

.footer-links a {
  display: inline-block;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.3s ease;
}

.footer-links a:hover {
  color: var(--ds-white);
}

.footer-links a:focus-visible {
  outline: 2px solid var(--ds-accent);
  outline-offset: 2px;
  border-radius: 2px;
}

/* Newsletter Section */
.footer-newsletter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  padding: 2rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.newsletter-heading {
  font-family: var(--ds-font-display);
  font-size: 1rem;
  font-weight: 600;
  color: var(--ds-white);
  margin-bottom: 0.25rem;
}

.newsletter-text {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.5);
}

.newsletter-form {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.newsletter-input {
  padding: 0.65rem 1rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: var(--ds-radius-md);
  color: var(--ds-white);
  font-size: 0.9rem;
  font-family: var(--ds-font-body);
  min-width: 240px;
  outline: none;
  transition: border-color 0.3s ease;
}

.newsletter-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.newsletter-input:focus {
  border-color: var(--ds-primary-500);
}

.newsletter-btn {
  padding: 0.65rem 1.5rem;
  background: var(--ds-primary-500);
  color: var(--ds-white);
  border: none;
  border-radius: var(--ds-radius-md);
  font-size: 0.9rem;
  font-weight: 600;
  font-family: var(--ds-font-body);
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.newsletter-btn:hover {
  background: var(--ds-primary-400);
  transform: translateY(-1px);
}

/* Tech Stack Section */
.footer-tech-stack {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 1rem 1.5rem;
  padding: 2rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.tech-stack-label {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.tech-stack-icons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 1.25rem;
}

.tech-stack-icons img {
  width: 28px;
  height: 28px;
  object-fit: contain;
  filter: brightness(0) invert(1);
  opacity: 0.5;
  transition: all 0.3s ease;
}

.tech-stack-icons img:hover {
  opacity: 1;
  transform: translateY(-2px);
}

/* Bottom Bar */
.footer-bottom {
  padding: 1.5rem 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.footer-copyright {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.4);
  margin: 0;
}

.footer-copyright strong {
  color: rgba(255, 255, 255, 0.7);
  font-weight: 600;
}

.footer-copyright-dot {
  margin: 0 0.4rem;
  color: rgba(255, 255, 255, 0.25);
}

.footer-copyright--legal {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.footer-copyright--legal a {
  color: rgba(255, 255, 255, 0.55);
  text-decoration: none;
  font-size: 0.82rem;
  transition: color 0.2s ease;
}

.footer-copyright--legal a:hover {
  color: var(--ds-white);
}

.footer-copyright--legal span {
  color: rgba(255, 255, 255, 0.2);
}

@media (max-width: 720px) {
  .footer-bottom {
    flex-direction: column;
    text-align: center;
  }
}

/* Responsive — brand on its own row, then four columns, two, one. */
@media (max-width: 1100px) {
  .footer-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 2rem;
  }

  .footer-brand {
    grid-column: 1 / -1;
  }

  .footer-description { max-width: 46ch; }
}

@media (max-width: 768px) {
  .footer-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {

  .footer-newsletter {
    flex-direction: column;
    align-items: flex-start;
  }

  .newsletter-form {
    width: 100%;
  }

  .newsletter-input {
    flex: 1;
    min-width: 0;
  }
}

@media (max-width: 480px) {
  .app-footer {
    padding: 3rem 0 0;
  }

  .footer-grid {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  .newsletter-form {
    flex-direction: column;
  }

  .newsletter-input {
    min-width: 0;
  }
}
</style>
